
import Vue from 'vue';
import EventService from '@/services/EventService';
import {TopColum, Trainee, TraineeType} from '@/components/types';
import fileDownload from 'js-file-download';
import {getStatus} from '@/dummy/dummydata';
import moment from 'moment';
import {mapState} from 'vuex';
import axios from 'axios';
import {saveAs} from 'file-saver';
import store, {GetScopeFromKey, Scope, ScopeKey} from '../store';
import type {User as AuthUser} from 'firebase/auth';

type Colum = {
  title: string;
  url: string;
  conf: TopColum;
  safetyEducationIndex: number;
};

type Item = {
  text: string;
  disabled: boolean;
  to: string;
};

type User = {
  id: string;
  done: boolean;
  name: string;
  kana?: string;
  conf: Trainee;
};

const scopeDescriptions = new Map<Scope, string>([
  ['truck', 'トラック'],
  ['forklift', 'フォークリフト'],
]);

const mainDescriptions = new Map<boolean, string>([
  [true, '12項目'],
  [false, 'ヒヤリハット'],
]);

function convertForbiddenFilenameCharToFullWidth(input: string): string {
  const FULLWIDTH_MAP: Record<string, string> = {
    '\\': '＼',
    '/': '／',
    ':': '：',
    '*': '＊',
    '?': '？',
    '"': '＂',
    '<': '＜',
    '>': '＞',
    '|': '｜',
  };
  // 正規表現で対象文字をまとめて検索し、マップに従って置換する
  return input.replace(/[\\/:*?"<>|]/g, (match: string) => {
    return FULLWIDTH_MAP[match] || match;
  });
}

type Data = {
  start: string;
  end: string;
  date: string;
  menu: boolean;
  items: Array<Item>;
  check: boolean;
  ruleDeadline: Array<(val: string) => string | boolean>;
  colum: Colum | null;
  doneUsers: Array<User>;
  doingUsers: Array<User>;
  progress: number;
  isLoading: boolean;
  snackbar: boolean;
  snackbarMessage: string;
  snackbarColor: string;
};

type Method = {
  download(): Promise<void>;
  setDeadline(): void;
  link(url: string, title: string): Promise<void>;
  isDev(): boolean;
  jump(): void;
};

type Computed = {
  isNoSubGroup: boolean;
  branchOfficeName: string;
  boGroupName: string;
  titleDesc: string;
  user: AuthUser | undefined;
  isGroup: boolean;
  accountName: string;
  currentName: string;
  currentScope: ScopeKey;
  noSubGroupStartYear: number;
};

type Props = {
  year: string;
  main: boolean;
};

const baseURL = EventService.ProtoURL();
const apiClient = axios.create({
  baseURL: baseURL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(config => {
  const companyID = store.state.current;
  if (companyID !== '') {
    config.headers['x-company-id'] = companyID;
  }
  return config;
});

export default Vue.extend<Data, Method, Computed, Props>({
  name: 'training-user-info',
  props: {
    year: {
      type: String,
      default: '',
    },
    main: {
      type: Boolean,
      default: true,
    },
  },
  async mounted() {
    if (this.user) {
      const token = await this.user.getIdToken();
      try {
        const {data} = await EventService.getYearInfo(token, Number(this.year));
        this.start = data.start;
        this.end = data.end;
      } catch (e) {
        console.log(e);
      }
      console.log(this.main);
      try {
        const {data: t} = await EventService.getChartByIDInfo(
          token,
          this.year,
          Boolean(this.main),
          GetScopeFromKey(this.currentScope),
          this.$route.params['id']
        );
        this.date = t.deadline;

        // 12項目/ヒヤリハット 区別 有り
        const defaultBreadcrumbText = this.main
          ? `研修項目(${this.year}年度)`
          : `ヒヤリハット(${this.year}年度)`;
        // 12項目/ヒヤリハット 区別 無し
        const noSubGroupBreadcrumbText = `${this.year}年度`;
        const breadcrumbText = !this.isNoSubGroup
          ? defaultBreadcrumbText
          : noSubGroupBreadcrumbText;
        this.items = [
          {
            text: breadcrumbText,
            disabled: false,
            to: `/colum?year=${this.year}&main=${this.main}`,
          },
          {
            text: t.detail,
            disabled: true,
            to: '',
          },
        ];

        t.users.forEach(user => {
          let status: TraineeType = user.status == 'done' ? 'done' : 'doing';
          const dayString = moment(user.lastUpdate).format('YYYY/MM/DD HH:mm');
          const uconf = Trainee(status, dayString);
          if (uconf) {
            if (status == 'done') {
              this.doneUsers.push({
                id: user.id,
                name: user.name,
                kana: user.kana,
                done: user.status == 'done',
                conf: uconf,
              });
            } else {
              this.doingUsers.push({
                id: user.id,
                name: user.name,
                kana: user.kana,
                done: user.status == 'done',
                conf: uconf,
              });
            }
          }
        });
        const sts = getStatus(t.deadline, t.total, t.done, this.main);
        const conf = TopColum(sts);
        if (conf) {
          this.colum = {
            title: t.detail,
            url: t.url,
            conf: conf,
            safetyEducationIndex: t.safetyEducationIndex,
          };
        }
        this.progress = t.progress;
      } catch (err) {
        console.log(err);
      }
    }
  },
  data: (): Data => ({
    start: '',
    end: '',
    check: false,
    items: [],
    date: '',
    menu: false,
    ruleDeadline: [(val: string) => !!val || '期限を設定してください'],
    colum: {
      title: '',
      url: '',
      conf: TopColum('doing'),
      safetyEducationIndex: 0,
    },
    doneUsers: [],
    doingUsers: [],
    progress: 0,
    isLoading: false,
    snackbar: false,
    snackbarMessage: '',
    snackbarColor: '',
  }),
  computed: {
    isNoSubGroup: function (): boolean {
      return (
        this.noSubGroupStartYear != 0 &&
        Number(this.year) >= this.noSubGroupStartYear
      );
    },
    branchOfficeName(): string {
      if (!this.isGroup) {
        return convertForbiddenFilenameCharToFullWidth(this.accountName);
      }
      return convertForbiddenFilenameCharToFullWidth(this.currentName);
    },
    boGroupName(): string {
      if (!this.isGroup) {
        return '';
      }
      return convertForbiddenFilenameCharToFullWidth(this.accountName);
    },
    titleDesc(): string {
      // 12項目の場合のみタイトルの前に番号を付加. ヒヤリハットはサーバー側でタイトルの前に`ID `を付加済み
      const id = this.main ? `${this.colum.safetyEducationIndex}-` : '';
      const ret = `-${id}${this.colum.title.replace(/ /g, '-')}`;
      return convertForbiddenFilenameCharToFullWidth(ret);
    },
    ...mapState([
      'user',
      'isGroup',
      'accountName',
      'currentName',
      'currentScope',
      'noSubGroupStartYear',
    ]),
  },
  methods: {
    async download() {
      if (this.user) {
        try {
          this.isLoading = true;
          const token = await this.user.getIdToken();
          const scope = GetScopeFromKey(this.currentScope);
          const {data} = await EventService.downloadChartByIDInfo(
            token,
            this.year,
            Boolean(this.main),
            scope,
            this.$route.params['id']
          );
          const mainDesc = this.isNoSubGroup
            ? ``
            : `-${mainDescriptions.get(Boolean(this.main))}`;
          const boGroupDesc =
            this.boGroupName === '' ? '' : `-${this.boGroupName}`;
          saveAs(
            new Blob([data]),
            `chart-${this.year}年度-${scopeDescriptions.get(scope)}${mainDesc}${
              this.titleDesc
            }${boGroupDesc}-${this.branchOfficeName}.xlsx`
          );
          this.snackbarMessage = 'ダウンロードが成功しました';
          this.snackbarColor = 'success';
          this.snackbar = true;
        } catch (error) {
          console.error('Download failed:', error);
          this.snackbarMessage = 'ダウンロードが失敗しました';
          this.snackbarColor = 'error';
          this.snackbar = true;
        } finally {
          this.isLoading = false;
        }
      }
    },
    setDeadline: async function () {
      if (this.user) {
        try {
          const token = await this.user.getIdToken();
          const id = this.$route.params['id'];
          await EventService.postDeadline(token, id, this.date);
        } catch (err) {
          console.log(err);
        }
      }
    },
    async link(url: string, title: string) {
      try {
        console.log(`usl:${url}`);
        this.isLoading = true;
        let {data} = await EventService.getReference(url);
        fileDownload(data, `${title}.pdf`);
        this.snackbarMessage = 'ダウンロードが成功しました';
        this.snackbarColor = 'success';
        this.snackbar = true;
      } catch (err) {
        console.log(err);
        this.snackbarMessage = 'ダウンロードが失敗しました';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.isLoading = false;
      }
    },
    isDev: function () {
      return this.main || process.env.VUE_APP_HIYARI_LIMIT == '1';
    },
    jump: function () {
      if (this.isDev()) {
        const id = this.$route.params['id'];
        this.$router.push(`/chart/${id}?year=${this.year}`);
      }
    },
  },
});
